/**
 * Plain 2D geometry for the scene: no DOM, no state. Rectangles are
 * `{ x, y, w, h }` with (x, y) the top-left corner.
 */

const r2 = (n) => Math.round(n * 100) / 100;

/**
 * Does segment a→b pass through the interior of rect r (shrunk by pad)?
 * Liang–Barsky clipping; touching the border does not count.
 */
export function segmentHitsRect(a, b, r, pad = 0.5) {
  const xmin = r.x + pad;
  const xmax = r.x + r.w - pad;
  const ymin = r.y + pad;
  const ymax = r.y + r.h - pad;
  if (xmin >= xmax || ymin >= ymax) return false;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  let t0 = 0;
  let t1 = 1;
  const clip = (p, q) => {
    if (Math.abs(p) < 1e-12) return q > 0; // parallel: inside iff q > 0
    const t = q / p;
    if (p < 0) { if (t > t1) return false; if (t > t0) t0 = t; } else { if (t < t0) return false; if (t < t1) t1 = t; }
    return true;
  };
  if (!clip(-dx, a.x - xmin)) return false;
  if (!clip(dx, xmax - a.x)) return false;
  if (!clip(-dy, a.y - ymin)) return false;
  if (!clip(dy, ymax - a.y)) return false;
  const len = Math.hypot(dx, dy);
  return t1 - t0 > 1e-9 && (t1 - t0) * len > 0.01;
}

/** Does any segment of the polyline pass through rect r? */
export function polylineHitsRect(points, r, pad) {
  for (let i = 1; i < points.length; i += 1) {
    if (segmentHitsRect(points[i - 1], points[i], r, pad)) return true;
  }
  return false;
}

export function distToSegment(p, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const l2 = dx * dx + dy * dy;
  const t = l2 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2)) : 0;
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

export function distToPolyline(p, points) {
  let best = Infinity;
  for (let i = 1; i < points.length; i += 1) best = Math.min(best, distToSegment(p, points[i - 1], points[i]));
  return best;
}

/** Points on a cubic Bézier, t = 0 … 1 in n equal steps (n + 1 points). */
export function cubicPoints(p0, c1, c2, p3, n) {
  const out = [];
  for (let i = 0; i <= n; i += 1) {
    const t = i / n;
    const u = 1 - t;
    const a = u * u * u;
    const b = 3 * u * u * t;
    const c = 3 * u * t * t;
    const d = t * t * t;
    out.push({ x: r2(a * p0.x + b * c1.x + c * c2.x + d * p3.x), y: r2(a * p0.y + b * c1.y + c * c2.y + d * p3.y) });
  }
  return out;
}

export function polylineD(points) {
  return points.map((p, i) => `${i ? 'L' : 'M'} ${r2(p.x)} ${r2(p.y)}`).join(' ');
}

/** Drop repeated and collinear middle points of an axis-aligned route. */
export function simplify(points) {
  const out = [];
  points.forEach((p) => {
    const last = out[out.length - 1];
    if (last && Math.abs(last.x - p.x) < 0.01 && Math.abs(last.y - p.y) < 0.01) return;
    out.push({ x: r2(p.x), y: r2(p.y) });
  });
  for (let i = out.length - 2; i >= 1; i -= 1) {
    const a = out[i - 1];
    const b = out[i];
    const c = out[i + 1];
    if ((Math.abs(a.x - b.x) < 0.01 && Math.abs(b.x - c.x) < 0.01) || (Math.abs(a.y - b.y) < 0.01 && Math.abs(b.y - c.y) < 0.01)) {
      out.splice(i, 1);
    }
  }
  return out;
}

export function rectsOverlap(a, b, pad = 0.5) {
  return a.x + pad < b.x + b.w && b.x + pad < a.x + a.w && a.y + pad < b.y + b.h && b.y + pad < a.y + a.h;
}
